import { useEffect, useState } from 'react'
import { registro, mensagemDoErro } from '../services/api'
import { Card, Titulo, Botao, Provedor, Etiqueta, Erro, Vazio } from '../components/ui'

/**
 * Trocar modelo e versão.
 *
 * Editor estruturado, e não um campo de JSON: o que se faz aqui é escolher um
 * modelo de uma lista, e digitar o nome à mão é a forma mais fácil de publicar
 * um identificador que não existe. A validação do coletor pegaria — mas o
 * momento de não errar é antes.
 *
 * O rascunho não vale nada até alguém publicar. É o ponto inteiro de haver
 * rascunho: dá para mexer à vontade sem que nada disso chegue nos produtos.
 */
export default function Editar() {
  const [rascunho, setRascunho] = useState(null)
  const [publicado, setPublicado] = useState(null)
  const [erro, setErro] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [razao, setRazao] = useState('')
  const [aviso, setAviso] = useState('')

  useEffect(() => { carregar() }, [])

  async function carregar() {
    try {
      const c = await registro.console()
      setPublicado(c.publicado)
      setRascunho(c.rascunho?.content ?? null)
    } catch (e) {
      setErro(mensagemDoErro(e))
    }
  }

  async function abrir() {
    setErro('')
    try {
      const d = await registro.abrirRascunho()
      setRascunho(d.content)
    } catch (e) { setErro(mensagemDoErro(e)) }
  }

  async function salvar(proximo) {
    setErro(''); setAviso(''); setSalvando(true)
    try {
      await registro.salvarRascunho(proximo)
      setRascunho(proximo)
      setAviso('Rascunho salvo. Nada mudou nos produtos ainda.')
    } catch (e) {
      // A validação recusou. O rascunho na tela CONTINUA com a edição — perder
      // o que a pessoa acabou de fazer por causa de um erro que ela vai
      // corrigir seria castigo, não proteção.
      setRascunho(proximo)
      setErro(mensagemDoErro(e))
    } finally { setSalvando(false) }
  }

  async function publicar() {
    setErro(''); setAviso('')
    try {
      const e = await registro.publicar(razao)
      setRazao('')
      setAviso(`Edição v${e.version} publicada. Os produtos passam a usá-la no próximo restart.`)
      await carregar()
    } catch (e) { setErro(mensagemDoErro(e)) }
  }

  if (erro && !rascunho && !publicado) return <Erro>{erro}</Erro>
  if (publicado === null && rascunho === null) return <Vazio>carregando…</Vazio>

  if (!rascunho) {
    return (
      <div className="flex flex-col gap-4">
        <Titulo sub="O rascunho parte da edição publicada, para você mexer numa linha de um documento que já funciona em vez de remontar o registro inteiro.">
          Nenhum rascunho aberto
        </Titulo>
        <div><Botao onClick={abrir}>Abrir rascunho a partir da v{publicado?.version ?? '—'}</Botao></div>
        <Erro>{erro}</Erro>
      </div>
    )
  }

  const modelos = Object.entries(rascunho.models ?? {})
  const provedores = rascunho.providers ?? {}
  // Motor medido nunca entra numa lista de escolha: ele não é ferramenta. Deixá-lo
  // no dropdown convidaria exatamente o erro que a validação recusa depois.
  const escolhiveis = modelos.filter(([, m]) => !m.measuredEngine && provedores[m.provider]?.api)

  const trocar = (papel, i, valor) => {
    const proximo = structuredClone(rascunho)
    proximo.roles[papel].cascade[i] = valor
    salvar(proximo)
  }

  const mudouDoPublicado = (papel) =>
    JSON.stringify(publicado?.content?.roles?.[papel]?.cascade) !==
    JSON.stringify(rascunho.roles[papel]?.cascade)

  const alterados = Object.keys(rascunho.roles ?? {}).filter(mudouDoPublicado)

  return (
    <div className="flex flex-col gap-6">
      <Titulo sub="Mudanças aqui não valem até você publicar. Os produtos continuam com a edição no ar.">
        Rascunho
      </Titulo>

      {aviso && (
        <div className="rounded-lg border border-pinho-100 bg-pinho-50 p-3 text-sm text-pinho-700">{aviso}</div>
      )}
      <Erro>{erro}</Erro>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-[11px] uppercase tracking-wide text-zinc-500">
              <th className="p-3 font-semibold">Papel</th>
              <th className="p-3 font-semibold">Principal</th>
              <th className="p-3 font-semibold">Reserva</th>
              <th className="p-3 font-semibold" />
            </tr>
          </thead>
          <tbody>
            {Object.entries(rascunho.roles ?? {}).map(([nome, papel]) => (
              <tr key={nome} className="border-b border-zinc-100 last:border-0">
                <td className="p-3">
                  <div className="font-mono text-xs font-bold text-pinho-600">{nome}</div>
                  <div className="max-w-[34ch] text-[11px] text-zinc-500">{papel.description}</div>
                </td>
                {[0, 1].map((i) => (
                  <td className="p-3" key={i}>
                    {papel.cascade.length > i || i === 0 ? (
                      <select
                        value={papel.cascade[i] ?? ''}
                        disabled={salvando || (i === 1 && papel.cascade.length === 1)}
                        onChange={(e) => trocar(nome, i, e.target.value)}
                        className="w-full rounded-lg border border-zinc-300 bg-white px-2 py-1.5 font-mono text-xs disabled:bg-zinc-50 disabled:text-zinc-400"
                      >
                        {escolhiveis.map(([id]) => (
                          <option key={id} value={id}>{id}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-xs text-zinc-300">—</span>
                    )}
                  </td>
                ))}
                <td className="p-3">
                  {mudouDoPublicado(nome) && <Etiqueta tom="aviso">alterado</Etiqueta>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold text-zinc-700">Catálogo</h3>
        <p className="max-w-[70ch] text-xs text-zinc-500">
          Provedor com <span className="font-mono">verified</span> em falso tem a URL escrita de
          memória e ainda não conferida na documentação dele — nenhum papel pode apontar para lá.
          Motor medido é consultado porque o cliente quer saber o que <em>ele</em> responde; trocá-lo
          mudaria o que está sendo medido, então ele não aparece nas escolhas acima.
        </p>
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <tbody>
              {modelos.map(([id, m]) => {
                const p = provedores[m.provider] ?? {}
                return (
                  <tr key={id} className="border-b border-zinc-100 last:border-0">
                    <td className="p-2.5 font-mono text-xs">{id}</td>
                    <td className="p-2.5"><Provedor id={m.provider} /></td>
                    <td className="p-2.5">
                      <div className="flex gap-1">
                        {m.measuredEngine && <Etiqueta tom="perigo">motor medido</Etiqueta>}
                        {!p.api && <Etiqueta tom="neutro">só catálogo</Etiqueta>}
                        {p.api && !p.verified && <Etiqueta tom="aviso">URL não conferida</Etiqueta>}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </Card>
      </section>

      <Card className="flex flex-col gap-3 p-5">
        <h3 className="text-sm font-semibold text-zinc-700">
          Publicar {alterados.length > 0 && `(${alterados.length} papel(éis) alterado(s))`}
        </h3>
        <p className="max-w-[70ch] text-xs text-zinc-500">
          Publicar torna esta edição imutável e a coloca no ar. Os produtos passam a usá-la no
          próximo restart deles. A razão fica no histórico — é o que responde, daqui a três meses,
          por que o modelo do atendimento mudou numa quinta-feira.
        </p>
        <textarea
          value={razao}
          onChange={(e) => setRazao(e.target.value)}
          rows={2}
          placeholder="Por que esta mudança? Ex: Sobe o raciocínio para o Opus 5 — a 4-5 saiu da tabela de preços."
          className="w-full rounded-lg border border-zinc-300 p-2.5 text-sm"
        />
        <div>
          <Botao onClick={publicar} disabled={razao.trim().length < 10}>
            Publicar
          </Botao>
          {razao.trim().length < 10 && (
            <span className="ml-3 text-xs text-zinc-400">a razão é obrigatória</span>
          )}
        </div>
      </Card>
    </div>
  )
}
